import { useState } from 'react'
import { Save, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { settings as settingsApi } from '@/utils/api'
import { formatDateTime } from '@/utils/formatters'
import { cn } from '@/lib/utils'

interface SettingsData {
  company_name?: string
  default_vacation_days?: number
  vacation_carry_over_deadline?: string
  vacation_reminder_enabled?: boolean
  vacation_reminder_date?: string
  [key: string]: unknown
}

interface AuditEntry {
  id: number
  timestamp: string
  user_name: string
  action: string
  entity_type: string
  changes?: { field: string; new_value: string }[]
}

const tabs = [
  { id: 'general', label: 'Allgemein' },
  { id: 'vacation', label: 'Urlaub' },
  { id: 'backup', label: 'Backup' },
  { id: 'audit', label: 'Änderungsprotokoll' },
] as const

type TabId = (typeof tabs)[number]['id']

export default function Einstellungen() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabId>('general')
  // Local editable copy of settings; seeded from the query result
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null)

  const { data: queryData, isLoading } = useQuery({
    queryKey: ['einstellungen'],
    queryFn: async () => {
      const [settingsRes, auditRes] = await Promise.all([
        settingsApi.get(),
        settingsApi.getAuditLog({ limit: 50 }),
      ])
      return { settings: settingsRes.data as SettingsData, auditLog: auditRes.data as AuditEntry[] }
    },
  })

  // Seed local editable state once data arrives (only if not yet set)
  if (queryData?.settings && settingsData === null) {
    setSettingsData(queryData.settings)
  }

  const auditLog: AuditEntry[] = queryData?.auditLog ?? []

  const saveMutation = useMutation({
    mutationFn: () => settingsApi.update(settingsData),
    onSuccess: () => {
      toast.success('Einstellungen gespeichert')
      queryClient.invalidateQueries({ queryKey: ['einstellungen'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Fehler beim Speichern')
    },
  })

  const handleSave = () => saveMutation.mutate()

  const handleBackup = async () => {
    try {
      const response = await settingsApi.createBackup()
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `compeer_backup_${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Backup erstellt')
    } catch {
      toast.error('Fehler beim Erstellen des Backups')
    }
  }

  const handleChange = (field: string, value: unknown) => {
    setSettingsData((prev) => prev ? { ...prev, [field]: value } : prev)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="Einstellungen" description="Systemkonfiguration und Backup" />

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <Card>
          <CardHeader>
            <CardTitle>Allgemeine Einstellungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Firmenname</label>
              <Input
                value={settingsData?.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Standard-Urlaubstage für neue Mitarbeiter</label>
              <Input
                type="number"
                min="0"
                max="50"
                value={settingsData?.default_vacation_days || 30}
                onChange={(e) => handleChange('default_vacation_days', parseInt(e.target.value) || 30)}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Vacation Tab */}
      {activeTab === 'vacation' && (
        <Card>
          <CardHeader>
            <CardTitle>Urlaubseinstellungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Uebertrag-Frist (Datum)</label>
              <Input
                value={settingsData?.vacation_carry_over_deadline || '31.03'}
                onChange={(e) => handleChange('vacation_carry_over_deadline', e.target.value)}
                placeholder="TT.MM"
              />
              <p className="text-xs text-muted-foreground">
                Bis zu diesem Datum muss Resturlaub aus dem Vorjahr genommen werden
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="reminder_enabled"
                checked={settingsData?.vacation_reminder_enabled || false}
                onChange={(e) => handleChange('vacation_reminder_enabled', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="reminder_enabled" className="text-sm">
                Automatische Erinnerung an Resturlaub aktivieren
              </label>
            </div>

            {settingsData?.vacation_reminder_enabled && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Erinnerungsdatum</label>
                <Input
                  value={settingsData?.vacation_reminder_date || '01.02'}
                  onChange={(e) => handleChange('vacation_reminder_date', e.target.value)}
                  placeholder="TT.MM"
                />
                <p className="text-xs text-muted-foreground">
                  An diesem Tag wird eine Erinnerung an alle mit Resturlaub gesendet
                </p>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <Card>
          <CardHeader>
            <CardTitle>Backup</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-muted-foreground">
              Erstellen Sie ein vollstaendiges Backup aller Daten im JSON-Format.
              Dieses Backup enthaelt alle Benutzer, Zeiteintraege, Bestellungen und Einstellungen.
            </p>
            <Button onClick={handleBackup}>
              <Download className="h-4 w-4" />
              Backup erstellen und herunterladen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && (
        <Card>
          <CardHeader>
            <CardTitle>Änderungsprotokoll</CardTitle>
            <CardDescription>Letzte 50 Änderungen</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Zeitpunkt</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Benutzer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Aktion</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bereich</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        Keine Einträge vorhanden
                      </td>
                    </tr>
                  ) : (
                    auditLog.map((log) => (
                      <tr key={log.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm">{formatDateTime(log.timestamp)}</td>
                        <td className="px-4 py-3 text-sm">{log.user_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={cn(
                              'capitalize',
                              log.action === 'delete' && 'text-destructive',
                              log.action === 'create' && 'text-green-600',
                              log.action === 'update' && 'text-blue-600'
                            )}
                          >
                            {log.action === 'create' ? 'Erstellt' : log.action === 'update' ? 'Aktualisiert' : 'Gelöscht'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm capitalize">{log.entity_type}</td>
                        <td className="max-w-xs truncate px-4 py-3 text-sm text-muted-foreground">
                          {log.changes?.map((c) => `${c.field}: ${c.new_value}`).join(', ') || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
